<template>
  <div class="disburstment-viewer-wrapper">
    <!-- Toast Notifications -->
    <div class="toast-container">
      <CToaster :autohide="3000">
        <CToast
          v-for="(info, index) in infoList"
          :key="`toast-${index}-${Date.now()}`"
          :show="true"
          :header="info.header"
          :color="info.color"
        >
          {{ info.message }}
        </CToast>
      </CToaster>
    </div>

    <!-- Page Header -->
    <CRow class="mb-4">
      <CCol>
        <div class="page-header">
          <h4 class="mb-0">
            <i class="fas fa-file-invoice-dollar me-2 text-primary"></i>
            <strong>Disbursement Documents - Stock No: {{ stock.stockNo }}</strong>
          </h4>
          <small class="text-muted">View and manage all disbursement-related documents and reports</small>
        </div>
      </CCol>
    </CRow>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="text-center py-5">
        <CSpinner color="primary" />
        <p class="text-muted mt-3">Loading disbursement documents...</p>
      </div>
    </div>

    <!-- Document Viewer -->
    <CRow v-else>
      <CCol sm="12">
        <CCard class="viewer-card">
          <CCardHeader class="viewer-header">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <i class="fas fa-folder-open me-2 text-primary"></i>
                <strong>Document Viewer</strong>
              </div>
              <CBadge color="info">
                <i class="fas fa-file me-1"></i>
                7 Documents
              </CBadge>
            </div>
          </CCardHeader>
          <CCardBody class="p-0">
            <!-- Document Tabs -->
            <CTabs add-tab-classes="mt-1" class="document-tabs">
              <!-- All Documents Tab -->
              <CTab active>
                <template slot="title">
                  <i class="fas fa-layer-group me-2"></i>
                  All Documents
                </template>
                <div class="tab-content-wrapper">
                  <WidgetsDisbursementMasterView
                    :company="company"
                    :bank="bank"
                    :company-bank-account="companyBankAccount"
                    :stock="stock"
                  />
                </div>
              </CTab>

              <!-- Disburse Letter Tab -->
              <CTab>
                <template slot="title">
                  <i class="fas fa-paper-plane me-2"></i>
                  Disburse Letter
                </template>
                <div class="tab-content-wrapper">
                  <WidgetsReportDocumentSubmission
                    :company="company"
                    :bank="bank"
                    :stock="stock"
                    :hide-controls="true"
                  />
                </div>
              </CTab>

              <!-- Customer Invoice Tab -->
              <CTab>
                <template slot="title">
                  <i class="fas fa-file-invoice me-2"></i>
                  Customer Invoice
                </template>
                <div class="tab-content-wrapper">
                  <WidgetsReportFullInvoice
                    :company="company"
                    :stock="stock"
                    :hide-controls="true"
                  />
                </div>
              </CTab>

              <!-- Invoice EOE Tab -->
              <CTab>
                <template slot="title">
                  <i class="fas fa-calculator me-2"></i>
                  Invoice EOE
                </template>
                <div class="tab-content-wrapper">
                  <WidgetsReportEoeInvoice
                    :company="company"
                    :bank="bank"
                    :stock="stock"
                    :hide-controls="true"
                  />
                </div>
              </CTab>

              <!-- Account Notice Tab -->
              <CTab>
                <template slot="title">
                  <i class="fas fa-clipboard-list me-2"></i>
                  Account Notice
                </template>
                <div class="tab-content-wrapper">
                  <WidgetsReportAccountNotice
                    :company="company"
                    :bank="bank"
                    :company-bank-account="companyBankAccount"
                    :stock="stock"
                    :hide-controls="true"
                  />
                </div>
              </CTab>

              <!-- Delivery Order Tab -->
              <CTab>
                <template slot="title">
                  <i class="fas fa-truck me-2"></i>
                  Delivery Order
                </template>
                <div class="tab-content-wrapper">
                  <WidgetsReportDeliveryOrder
                    :company="company"
                    :stock="stock"
                    :hide-controls="true"
                  />
                </div>
              </CTab>

              <!-- E-Hakmilik Letter Tab -->
              <CTab>
                <template slot="title">
                  <i class="fas fa-file-contract me-2"></i>
                  E-Hakmilik Letter
                </template>
                <div class="tab-content-wrapper">
                  <WidgetsReportEHakmilik
                    :company="company"
                    :bank="bank"
                    :stock="stock"
                    :hide-controls="true"
                  />
                </div>
              </CTab>
            </CTabs>
          </CCardBody>

          <!-- Viewer Footer -->
          <CCardFooter class="viewer-footer">
            <div class="d-flex justify-content-between align-items-center">
              <CButton
                color="secondary"
                @click="cancel"
              >
                <i class="fas fa-arrow-left me-2"></i>
                Back to List
              </CButton>
              <small class="text-muted">
                <i class="fas fa-info-circle me-1"></i>
                Switch between tabs to view different documents
              </small>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import CompanyApi from "../../lib/companyApi";
import StockApi from "../../lib/stockApi";
import WidgetsReportAccountNotice from "../widgets/WidgetsReportAccountNotice";
import WidgetsReportFullInvoice from "../widgets/WidgetsReportFullInvoice";
import WidgetsReportDocumentSubmission from "../widgets/WidgetsReportDocumentSubmission";
import WidgetsReportEoeInvoice from "../widgets/WidgetsReportEoeInvoice";
import WidgetsReportDeliveryOrder from "../widgets/WidgetsReportDeliveryOrder";
import WidgetsDisbursementMasterView from "../widgets/WidgetsDisbursementMasterView";
import WidgetsReportEHakmilik from "../widgets/WidgetsReportEHakmilik";

export default {
  name: "Disburstment",
  components: {
    WidgetsReportAccountNotice,
    WidgetsReportFullInvoice,
    WidgetsReportDocumentSubmission,
    WidgetsReportEoeInvoice,
    WidgetsReportDeliveryOrder,
    WidgetsDisbursementMasterView,
    WidgetsReportEHakmilik,
  },
  data() {
    return {
      stockApi: new StockApi(),
      stock: {},
      company: {
        name: "",
        regNo: "",
        tagLine: "",
        address: "",
        phone: "",
        logoUrl: "",
        bankAccountName: "",
        bankAccountNo: "",
        bankName: "",
        bankAddress: "",
      },
      bank: {},
      companyBankAccount: {},
      infoList: [],
      loading: false,
    };
  },
  mounted() {
    this.resetStockObj();
  },
  methods: {
    toast(header, message, color) {
      this.infoList.push({
        header: header,
        message: message,
        color: color,
      });
    },

    resetStockObj() {
      this.loading = true;
      this.stockApi
        .get(this.$route.params.id)
        .then((response) => {
          this.stock = response.result;
          this.company = this.stock.apCompany.subCompany;
          this.bank = this.stock.sale?.loan?.bank || {};
          this.companyBankAccount = this.stock.apCompany?.bankAccount || {};
          this.loading = false;
        })
        .catch(({ data }) => {
          this.toast("Error", helper.getErrorMessage(data), "danger");
          this.loading = false;
        });
    },

    cancel() {
      this.$router.push({ path: "/admins/DisburstmentList" });
    },
  },
};
</script>

<style scoped>
/* Professional Container */
.disburstment-viewer-wrapper {
  padding: 1.5rem;
  min-height: 100vh;
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

/* Page Header */
.page-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
}

.page-header h4 {
  font-size: 1.5rem;
  color: var(--cui-body-color, #212529);
}

.page-header small {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
}

/* Loading State */
.loading-state {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Viewer Card */
.viewer-card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.viewer-header {
  background: white;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.25rem;
}

.viewer-header strong {
  font-size: 1.1rem;
  color: var(--cui-body-color, #212529);
}

/* Document Tabs */
.document-tabs {
  min-height: 500px;
}

.tab-content-wrapper {
  padding: 1.5rem;
}

/* Tab Icons */
.nav-tabs .nav-link {
  font-weight: 500;
}

.nav-tabs .nav-link i {
  opacity: 0.7;
}

.nav-tabs .nav-link.active i {
  opacity: 1;
}

/* Viewer Footer */
.viewer-footer {
  background: var(--cui-body-bg, #f8f9fa);
  border-top: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.25rem;
}

/* Responsive Adjustments */
@media (max-width: 767.98px) {
  .disburstment-viewer-wrapper {
    padding: 1rem;
  }

  .page-header h4 {
    font-size: 1.25rem;
  }

  .tab-content-wrapper {
    padding: 1rem;
  }

  /* Stack tab titles on mobile */
  .nav-tabs .nav-link {
    font-size: 0.875rem;
    padding: 0.5rem 0.75rem;
  }

  .nav-tabs .nav-link i {
    display: none;
  }
}

/* Print Styles */
@media print {
  .toast-container,
  .viewer-footer,
  .nav-tabs {
    display: none !important;
  }

  .viewer-card {
    box-shadow: none;
    border: 1px solid #dee2e6;
  }

  .tab-content-wrapper {
    padding: 0;
  }
}
</style>
